/**
 * Pure formatting of the "what will happen" block (KAN-595).
 *
 * Per-kind presentation lives in a registry (PRESENTERS): a new kind is a new
 * entry, not a new `if kind ===` branch.
 */
import type { CampaignKind, OutcomePreview } from '@/types/dataset';

/** Russian plural: [1 штука, 2-4 штуки, 5+ штук]. */
export function pluralRu(n: number, forms: [string, string, string]): string {
  const abs = Math.abs(n);
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1];
  return forms[2];
}

export const accountsLabel = (n: number): string =>
  `${n} ${pluralRu(n, ['аккаунт', 'аккаунта', 'аккаунтов'])}`;

const WARNING_TEXTS: Record<string, string> = {
  no_templates: 'Не задан текст первого сообщения: отправка не начнётся',
  no_senders: 'Нет аккаунтов с включённой рассылкой: отправлять некому',
};

/** Human text of a warning code; an unknown code is shown as is. */
export const warningText = (code: string): string =>
  WARNING_TEXTS[code] ?? code;

export interface OutcomeView {
  /** What the settings give: one line per fact. */
  facts: string[];
  /** Fixed explanations of the kind. */
  notes: string[];
  warnings: string[];
  /** Reason why the launch is impossible (red). */
  error: string | null;
}

type Facts = (p: OutcomePreview) => string[];

const windowFact =
  (label: string): Facts =>
  (p) =>
    p.window
      ? [
          `${label}: ${p.window.start}-${p.window.end}${
            p.timezone ? `, часовой пояс ${p.timezone}` : ''
          }`,
        ]
      : [];

const sendersFact: Facts = (p) => {
  if (p.senders_count == null) return [];
  if (p.senders_count === 0) return ['Аккаунтов с включённой рассылкой: 0'];
  let text = `Сообщения отправят ${accountsLabel(p.senders_count)} с включённой рассылкой`;
  const limit = p.daily_limit_per_account;
  if (limit != null) {
    const chats = pluralRu(limit, ['нового чата', 'новых чата', 'новых чатов']);
    text += `; не больше ${limit} ${chats} в сутки на аккаунт`;
  }
  if (p.daily_capacity != null) {
    text += `${limit != null ? ',' : ';'} всего до ${p.daily_capacity} в сутки`;
  }
  return [text];
};

const delayFact: Facts = (p) => {
  const { delay_min_s: min, delay_max_s: max } = p;
  if (min == null || max == null) return [];
  const range = min === max ? `${min}` : `${min}-${max}`;
  return [`Пауза между сообщениями: ${range} с`];
};

const countFact =
  (label: string, pick: (p: OutcomePreview) => number | null): Facts =>
  (p) => {
    const n = pick(p);
    return n == null ? [] : [`${label}: ${n}`];
  };

interface Presenter {
  facts: Facts[];
  notes: string[];
}

const PRESENTERS: Record<CampaignKind, Presenter> = {
  voice: {
    facts: [windowFact('Окно звонков')],
    notes: ['Дальше звонит голосовой бот по сценарию выбранной роли.'],
  },
  telegram_outreach: {
    facts: [
      sendersFact,
      windowFact('Окно отправки'),
      delayFact,
      countFact('Шаблонов первого сообщения', (p) => p.templates_count),
      countFact('Стоп-слов', (p) => p.stop_words_count),
    ],
    notes: [
      'Если человека нет в Telegram, запись получает статус "Нет в Telegram" и в обзвон автоматически не переходит.',
      'Телефон из колонки phone нужен для ручной эскалации.',
      'Настройки берутся из вашего кабинета (канал Telegram) и меняют поведение и уже созданных кампаний.',
    ],
  },
};

export function describeOutcome(preview: OutcomePreview): OutcomeView {
  const presenter = PRESENTERS[preview.kind] ?? PRESENTERS.voice;
  return {
    facts: presenter.facts.flatMap((build) => build(preview)),
    notes: presenter.notes,
    warnings: preview.warnings.map(warningText),
    error: preview.activation_error,
  };
}
